import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { QRCodeCanvas } from 'qrcode.react';
import { useAuth } from '@/hooks/useAuth';
import { VersionFooter } from '@/components/layout';

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const INPUT_CLASS =
  'w-full rounded-lg border-[1.5px] border-gray-300 px-4 py-3 text-sm outline-none transition-colors focus:border-[#6B0F1A]';

/**
 * L'etape courante de la connexion (#87).
 *
 * Le mot de passe seul n'ouvre plus la console : il mene soit a une demande de
 * code, soit a un enrolement. Les identifiants sont gardes en memoire le temps
 * du second appel — la connexion n'est pas coupee en deux moities dont la
 * premiere vaudrait quelque chose cote serveur.
 */
type Step =
  | { name: 'credentials' }
  | { name: 'mfa'; email: string; password: string; hasBackupCodes: boolean }
  | { name: 'enroll'; otpQrLink: string; enrollmentTicket: string }
  | { name: 'backupCodes'; codes: string[] };

export function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, login, completeEnrollment, error } = useAuth();

  const [step, setStep] = useState<Step>({ name: 'credentials' });
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod 3/4 type mismatch with @hookform/resolvers
    resolver: zodResolver(loginSchema as any),
  });

  // La redirection ne vaut que pour une session ouverte par le chemin
  // ordinaire. Pendant l'enrolement, c'est `onSubmitEnrollment` qui decide de
  // la suite : le jeton est range avant qu'il ait pu passer a l'ecran des
  // codes de secours, et une redirection automatique ferait disparaitre des
  // codes montres une seule fois.
  useEffect(() => {
    const awaitsRedirect = step.name === 'credentials' || step.name === 'mfa';
    if (isAuthenticated && awaitsRedirect) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, step.name, navigate]);

  const onSubmitCredentials = async (data: LoginFormData) => {
    setStepError(null);
    const outcome = await login(data.email, data.password);

    if (outcome.kind === 'mfa') {
      setStep({
        name: 'mfa',
        email: data.email,
        password: data.password,
        hasBackupCodes: outcome.hasBackupCodes,
      });
    } else if (outcome.kind === 'enroll') {
      setStep({
        name: 'enroll',
        otpQrLink: outcome.otpQrLink,
        enrollmentTicket: outcome.enrollmentTicket,
      });
    }
  };

  const onSubmitCode = async () => {
    if (step.name !== 'mfa') return;
    setStepError(null);

    const outcome = await login(
      step.email,
      step.password,
      code.trim(),
      useBackupCode ? 'backupCode' : 'app',
    );

    if (outcome.kind !== 'signed-in') {
      setCode('');
    }
  };

  const onSubmitEnrollment = async () => {
    if (step.name !== 'enroll') return;
    setStepError(null);

    try {
      const backupCodes = await completeEnrollment(step.enrollmentTicket, code.trim());
      setCode('');

      if (backupCodes && backupCodes.length > 0) {
        setStep({ name: 'backupCodes', codes: backupCodes });
        return;
      }
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const apiErr = err as { message?: string | string[] };
      setStepError(
        typeof apiErr.message === 'string' ? apiErr.message : 'Invalid verification code',
      );
      setCode('');
    }
  };

  const submitLabel = isLoading ? 'Signing in…' : 'Sign In';

  return (
    <div
      className="flex min-h-screen items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #6B0F1A 0%, #3D0008 100%)',
      }}
    >
      <div className="w-[400px] rounded-[14px] bg-white p-12 text-center shadow-2xl">
        <div className="mb-1 text-[32px] font-extrabold tracking-wide" style={{ color: '#6B0F1A' }}>
          <span className="mr-1.5 text-[22px]">🏦</span> CBS
        </div>
        <div className="mb-9 text-[13px] uppercase tracking-[2px] text-gray-400">
          Core Banking System
        </div>

        {step.name === 'credentials' && (
          <form onSubmit={handleSubmit(onSubmitCredentials)} noValidate>
            <div className="mb-3.5">
              <label htmlFor="emailInput" className="sr-only">
                Email
              </label>
              <input
                {...register('email')}
                id="emailInput"
                type="email"
                placeholder="Email"
                autoComplete="off"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className={INPUT_CLASS}
              />
              {errors.email && (
                <p id="email-error" className="mt-1 text-left text-[13px] text-red-700">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="mb-3.5">
              <label htmlFor="passwordInput" className="sr-only">
                Password
              </label>
              <input
                {...register('password')}
                id="passwordInput"
                type="password"
                placeholder="Password"
                aria-invalid={!!errors.password}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className={INPUT_CLASS}
              />
              {errors.password && (
                <p id="password-error" className="mt-1 text-left text-[13px] text-red-700">
                  {errors.password.message}
                </p>
              )}
            </div>

            <SubmitButton isLoading={isLoading} label={submitLabel} />
          </form>
        )}

        {step.name === 'mfa' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmitCode();
            }}
            noValidate
          >
            <p className="mb-4 text-[13px] text-gray-600">
              {useBackupCode
                ? 'Enter one of your backup codes.'
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>

            <label htmlFor="codeInput" className="sr-only">
              Verification code
            </label>
            <input
              id="codeInput"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={useBackupCode ? 'abcd efgh ijkl' : '123456'}
              autoComplete="one-time-code"
              autoFocus
              className={INPUT_CLASS}
            />

            <SubmitButton isLoading={isLoading} label={isLoading ? 'Verifying…' : 'Verify'} />

            {step.hasBackupCodes && (
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode((v) => !v);
                  setCode('');
                }}
                className="mt-3 cursor-pointer border-none bg-transparent text-[13px] text-gray-500 underline"
              >
                {useBackupCode ? 'Use my authenticator app' : 'Use a backup code instead'}
              </button>
            )}
          </form>
        )}

        {step.name === 'enroll' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmitEnrollment();
            }}
            noValidate
          >
            <p className="mb-4 text-left text-[13px] text-gray-600">
              Two-factor authentication is required. Scan this code with your authenticator app,
              then enter the 6-digit code it shows.
            </p>

            <div className="mb-4 flex justify-center">
              <QRCodeCanvas value={step.otpQrLink} size={168} />
            </div>

            <label htmlFor="enrollCodeInput" className="sr-only">
              Verification code
            </label>
            <input
              id="enrollCodeInput"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              autoComplete="one-time-code"
              autoFocus
              className={INPUT_CLASS}
            />

            <SubmitButton isLoading={isLoading} label={isLoading ? 'Verifying…' : 'Confirm'} />
          </form>
        )}

        {step.name === 'backupCodes' && (
          <div>
            <p className="mb-4 text-left text-[13px] text-gray-600">
              Save these backup codes somewhere safe. They are shown once, and each one works a
              single time. They are the only way back in if you lose your phone.
            </p>

            {/* Une seule colonne, et chaque code numerote.
                Sur deux colonnes, les espaces qui decoupent un code se
                confondaient avec celui qui separait les colonnes : on ne
                voyait plus ou un code finissait. */}
            <ol className="mb-5 rounded-lg bg-gray-50 p-3 text-left font-mono text-[13px]">
              {step.codes.map((backupCode, index) => (
                <li key={backupCode} className="flex gap-3 py-0.5">
                  <span className="w-5 shrink-0 text-right text-gray-400">{index + 1}</span>
                  <span>{backupCode}</span>
                </li>
              ))}
            </ol>

            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              className="w-full cursor-pointer rounded-lg border-none py-3 text-[15px] font-semibold text-white"
              style={{ backgroundColor: '#6B0F1A' }}
            >
              I have saved them
            </button>
          </div>
        )}

        {(error ?? stepError) && (
          <p id="api-error" role="alert" className="mt-3.5 text-[13px] text-red-700">
            {stepError ?? error}
          </p>
        )}
      </div>
      <VersionFooter />
    </div>
  );
}

function SubmitButton({ isLoading, label }: { isLoading: boolean; label: string }) {
  return (
    <button
      type="submit"
      disabled={isLoading}
      className="mt-2 w-full cursor-pointer rounded-lg border-none py-3 text-[15px] font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
      style={{ backgroundColor: isLoading ? '#520b14' : '#6B0F1A' }}
      onMouseEnter={(e) => {
        if (!isLoading) e.currentTarget.style.backgroundColor = '#520b14';
      }}
      onMouseLeave={(e) => {
        if (!isLoading) e.currentTarget.style.backgroundColor = '#6B0F1A';
      }}
    >
      {label}
    </button>
  );
}
